<template>
  <div class="kds-topbar">
    <div class="kds-topbar-left">
      <button class="kds-icon-btn" @click="goBack" :title="$t('back')">
        <v-icon color="white">mdi-arrow-left</v-icon>
      </button>
      <img
        src="https://restorani.bitlab.host/img/o.png"
        alt="Ordera"
        class="kds-logo mx-2"
      />
      <div class="kds-title-block">
        <div class="kds-title">{{ $t("kitchen_display") }}</div>
        <div class="kds-subtitle">
          {{ restaurantName || $t("kitchen_display_subtitle") }}
        </div>
      </div>
    </div>

    <div class="kds-topbar-center">
      <button
        v-for="tab in statusTabs"
        :key="tab.value"
        class="kds-status-tab"
        :class="{ active: statusView === tab.value }"
        :style="statusView === tab.value ? { backgroundColor: tab.accent } : {}"
        @click="$emit('update-status-view', tab.value)"
      >
        <v-icon small class="mr-1">{{ tab.icon }}</v-icon>
        <span>{{ tab.label }}</span>
        <span class="kds-status-tab-count">{{ tab.count }}</span>
      </button>
    </div>

    <div class="kds-topbar-right">
      <div class="kds-date-control">
        <button
          class="kds-filter-chip kds-date-chip"
          :class="{ active: dateMode === 'last24h' }"
          @click="$emit('set-last24h')"
        >
          <v-icon small class="mr-1">mdi-history</v-icon>
          {{ $t("last_24h") }}
        </button>
        <div class="kds-date-input-wrap" :class="{ active: dateMode === 'custom' }">
          <button class="kds-date-arrow" @click="$emit('shift-custom-date', -1)">
            <v-icon x-small color="white">mdi-chevron-left</v-icon>
          </button>
          <input
            type="date"
            class="kds-date-input"
            :value="customDate"
            @input="$emit('custom-date-change', $event.target.value)"
          />
          <button class="kds-date-arrow" @click="$emit('shift-custom-date', 1)">
            <v-icon x-small color="white">mdi-chevron-right</v-icon>
          </button>
        </div>
      </div>
      <div class="kds-clock">
        <div class="kds-clock-time">{{ clockText }}</div>
        <div class="kds-clock-date">{{ clockDateText }}</div>
      </div>
      <button
        class="kds-icon-btn"
        :class="{ spinning: isLoading }"
        @click="$emit('refresh')"
        :title="$t('tooltip_refresh_report')"
      >
        <v-icon color="white">mdi-refresh</v-icon>
      </button>
      <v-menu offset-y left :close-on-content-click="false">
        <template v-slot:activator="{ on, attrs }">
          <button
            class="kds-icon-btn"
            v-bind="attrs"
            v-on="on"
            :title="$t('kds_density_settings')"
          >
            <v-icon color="white">mdi-tune-variant</v-icon>
          </button>
        </template>
        <v-card class="kds-settings-card" dark>
          <v-card-title class="kds-settings-title">{{
            $t("kds_density_settings")
          }}</v-card-title>
          <v-card-text>
            <div class="kds-settings-row">
              <div class="kds-settings-label">
                <v-icon small class="mr-2">mdi-format-size</v-icon>
                {{ $t("kds_font_size") }}
              </div>
              <v-btn-toggle
                v-model="fontScaleProxy"
                mandatory
                dense
                color="primary"
                class="kds-settings-toggle"
              >
                <v-btn small :value="1">{{ $t("kds_font_size_normal") }}</v-btn>
                <v-btn small :value="1.15">{{ $t("kds_font_size_large") }}</v-btn>
                <v-btn small :value="1.3">{{ $t("kds_font_size_xlarge") }}</v-btn>
              </v-btn-toggle>
            </div>

            <div class="kds-settings-subtitle">{{ $t("kds_visible_columns") }}</div>
            <div
              v-for="group in kdsGroupDefs"
              :key="'vis-' + group.key"
              class="kds-settings-row"
            >
              <div class="kds-settings-label">
                <v-icon small :color="group.accent" class="mr-2">{{
                  group.icon
                }}</v-icon>
                {{ $t(group.labelKey) }}
              </div>
              <v-switch
                :input-value="isGroupVisible(group.key)"
                dense
                hide-details
                inset
                color="primary"
                class="kds-settings-switch"
                @change="toggleGroupVisibility(group.key)"
              />
            </div>

            <div
              v-for="group in kdsGroupDefs"
              :key="group.key"
              class="kds-settings-row"
              :class="{ 'kds-settings-row--disabled': !isGroupVisible(group.key) }"
            >
              <div class="kds-settings-label">
                <v-icon small :color="group.accent" class="mr-2">{{
                  group.icon
                }}</v-icon>
                {{ $t(group.labelKey) }}
              </div>
              <v-btn-toggle
                v-model="densitySettings[group.key]"
                mandatory
                dense
                color="primary"
                class="kds-settings-toggle"
                :disabled="!isGroupVisible(group.key)"
              >
                <v-btn small value="full">{{ $t("kds_density_full") }}</v-btn>
                <v-btn small value="compact">{{ $t("kds_density_compact") }}</v-btn>
                <v-btn small value="minimal">{{ $t("kds_density_minimal") }}</v-btn>
              </v-btn-toggle>
            </div>
          </v-card-text>
        </v-card>
      </v-menu>
      <button
        class="kds-icon-btn"
        @click="$emit('toggle-fullscreen')"
        :title="$t('kitchen_display')"
      >
        <v-icon color="white">{{
          isFullscreen ? "mdi-fullscreen-exit" : "mdi-fullscreen"
        }}</v-icon>
      </button>
    </div>
  </div>
</template>

<script>
import { KDS_GROUPS } from "./kdsConstants";

export default {
  name: "KdsTopbar",
  props: {
    statusView: { type: String, default: "all" },
    dateMode: { type: String, default: "last24h" },
    customDate: { type: String, default: "" },
    isLoading: { type: Boolean, default: false },
    isFullscreen: { type: Boolean, default: false },
    fontScale: { type: Number, default: 1 },
    densitySettings: { type: Object, required: true },
    visibleGroups: { type: Array, required: true },
    groupedTickets: { type: Object, required: true },
    totalTicketsCount: { type: Number, default: 0 },
  },
  data() {
    return {
      now: Date.now(),
      clockTimer: null,
    };
  },
  computed: {
    restaurantName() {
      return this.toLatin(this.$store.state.restaurant?.name || "");
    },
    kdsGroupDefs() {
      return KDS_GROUPS;
    },
    statusTabs() {
      const tabs = KDS_GROUPS.filter((group) => this.visibleGroups.includes(group.key)).map(
        (group) => ({
          value: group.key,
          label: this.$t(group.labelKey),
          icon: group.icon,
          accent: group.accent,
          count: (this.groupedTickets[group.key] || []).length,
        })
      );

      tabs.push({
        value: "all",
        label: this.$t("kds_view_all"),
        icon: "mdi-view-column-outline",
        accent: "#3b5bfd",
        count: this.totalTicketsCount,
      });

      return tabs;
    },
    clockText() {
      return new Date(this.now).toLocaleTimeString("sr-Latn", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    clockDateText() {
      return new Date(this.now).toLocaleDateString("sr-Latn", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },
    fontScaleProxy: {
      get() {
        return this.fontScale;
      },
      set(value) {
        this.$emit("update:font-scale", value);
      },
    },
  },
  mounted() {
    this.clockTimer = setInterval(() => {
      this.now = Date.now();
    }, 1000);
  },
  beforeDestroy() {
    clearInterval(this.clockTimer);
  },
  methods: {
    goBack() {
      this.$router.push({ name: "guest-report" });
    },
    isGroupVisible(key) {
      return this.visibleGroups.includes(key);
    },
    toggleGroupVisibility(key) {
      const isVisible = this.visibleGroups.includes(key);
      // Always keep at least one column visible.
      if (isVisible && this.visibleGroups.length <= 1) return;

      const next = isVisible
        ? this.visibleGroups.filter((k) => k !== key)
        : [...this.visibleGroups, key];

      this.$emit("update:visible-groups", next);
    },
  },
};
</script>

<style scoped>
.kds-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 18px;
  background: #1c2030;
  border-bottom: 1px solid #2a2f42;
  flex-wrap: wrap;
  gap: 8px;
}

.kds-topbar-left {
  display: flex;
  align-items: center;
}

.kds-logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
  flex-shrink: 0;
}

.kds-title-block {
  line-height: 1.15;
}

.kds-title {
  color: #fff;
  font-size: 20px;
  font-weight: 700;
}

.kds-subtitle {
  color: #9aa0b4;
  font-size: 12px;
}

.kds-topbar-center {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.kds-status-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #333952;
  background: #232842;
  color: #cfd3e6;
  border-radius: 20px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.kds-status-tab.active {
  color: #fff;
  border-color: transparent;
}

.kds-status-tab-count {
  background: rgba(255, 255, 255, 0.18);
  border-radius: 10px;
  padding: 0 7px;
  font-size: 12px;
}

.kds-topbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.kds-date-control {
  display: flex;
  align-items: center;
  gap: 6px;
}

.kds-filter-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #333952;
  background: #232842;
  color: #cfd3e6;
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.kds-filter-chip.active {
  background: #3b5bfd;
  border-color: #3b5bfd;
  color: #fff;
}

.kds-date-chip {
  white-space: nowrap;
}

.kds-date-input-wrap {
  display: flex;
  align-items: center;
  gap: 2px;
  border: 1px solid #333952;
  background: #232842;
  border-radius: 20px;
  padding: 2px;
}

.kds-date-input-wrap.active {
  border-color: #3b5bfd;
}

.kds-date-arrow {
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.kds-date-input {
  background: transparent;
  border: none;
  color: #cfd3e6;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 2px;
  width: 122px;
  color-scheme: dark;
}

.kds-date-input:focus {
  outline: none;
}

.kds-clock {
  text-align: right;
  color: #fff;
  margin-right: 4px;
}

.kds-clock-time {
  font-size: 18px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.kds-clock-date {
  font-size: 11px;
  color: #9aa0b4;
}

.kds-icon-btn {
  background: #232842;
  border: 1px solid #333952;
  border-radius: 8px;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.kds-icon-btn.spinning .v-icon {
  animation: kds-spin 0.8s linear infinite;
}

@keyframes kds-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.kds-settings-card {
  background: #1c2030 !important;
  min-width: 340px;
}

.kds-settings-title {
  font-size: 14px;
  font-weight: 700;
}

.kds-settings-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #2a2f42;
}

.kds-settings-row:last-child {
  border-bottom: none;
}

.kds-settings-label {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: #e6e8f2;
  white-space: nowrap;
}

.kds-settings-toggle {
  flex-shrink: 0;
}

.kds-settings-subtitle {
  margin-top: 8px;
  padding-top: 10px;
  border-top: 1px solid #2a2f42;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9aa0b4;
}

.kds-settings-switch {
  flex-shrink: 0;
  margin: 0;
  padding: 0;
}

.kds-settings-row--disabled .kds-settings-label {
  opacity: 0.5;
}
</style>
